import { IHeaderAngularComp } from '@ag-grid-community/angular';
import { IHeaderParams } from '@ag-grid-community/core';
import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';

import { ButtonComponent } from '@shared/components/button/button.component';
import { InputComponent } from '@shared/components/input/input.component';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import { ClickOutsideDirective } from '@shared/directives/click-outside.directive';
import { TooltipOverflowDirective } from '@shared/directives/tooltip-overflow.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { OverlayService } from '@shared/services/overlay.service';

export interface AgInlineHeaderCustomParams {
  isHeaderEditable?: boolean;
  isNewColumn?: boolean;
  onDelete?: () => void;
  onRename?: (newTitle: string) => void;
  removeColumnDisabled?: boolean;
  removeColumnTooltip?: string;
  title: string;
}

interface AgInlineHeaderParams extends AgInlineHeaderCustomParams, IHeaderParams {}

@Component({
  host: {
    class: 'block w-full h-full',
  },
  imports: [
    FormsModule,
    InputComponent,
    TooltipOverflowDirective,
    TooltipDirective,
    ButtonComponent,
    ClickOutsideDirective,
  ],
  templateUrl: './ag-inline-header.component.html',
})
export class AgInlineHeaderComponent implements IHeaderAngularComp {
  deleteColumnConfirmationModal = injectDeleteColumnConfirmationModal();

  editing = signal(false);
  titleSignal = signal('');

  params!: AgInlineHeaderParams;

  agInit(params: AgInlineHeaderParams) {
    this.params = params;
    this.titleSignal.set(params.title);

    // Auto-start editing for new columns
    if (params.isNewColumn) {
      setTimeout(() => this.startEditing(), 0);
    }
  }

  finishEditing = (newTitle: string) => {
    const trimmedTitle = newTitle.trim();

    this.editing.set(false);
    this.titleSignal.set(trimmedTitle);

    if (this.params.onRename) {
      this.params.onRename(trimmedTitle);
    }
  };

  handleDelete = async (event: MouseEvent) => {
    event.stopPropagation();

    if (this.params.removeColumnDisabled) {
      return;
    }

    const confirmed = this.params.isNewColumn ? true : await this.deleteColumnConfirmationModal();

    if (confirmed && this.params.onDelete) {
      this.params.onDelete();
    }
  };

  isOverflowing = (el: HTMLElement | null): boolean => {
    if (!el) return false;

    return el.scrollWidth > el.offsetWidth;
  };

  refresh(): boolean {
    return true;
  }

  startEditing = () => {
    if (!this.params.isHeaderEditable) {
      return;
    }

    this.editing.set(true);
  };
}

const injectDeleteColumnConfirmationModal = () => {
  const overlayService = inject(OverlayService);

  return async () => {
    const ref = overlayService.openPopup<
      { maxWidth: number; message: string },
      boolean | undefined,
      ConfirmationModalComponent
    >({
      content: ConfirmationModalComponent,
      data: {
        maxWidth: 430,
        message:
          'Deleting this attribute column will delete the attribute column for the entire vendor budget. Are you sure you want to continue?',
      },
      settings: {
        header: 'Delete Attribute?',
        primaryButton: { label: 'Delete' },
        secondaryButton: { label: 'Go Back' },
      },
    });

    const { data: refData } = await firstValueFrom(ref.afterClosed$);

    return !!refData;
  };
};
